import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { defaultCurveDisplaySettings } from '@/store/well-logs/tabletSettings/config';
import { isDefined } from '@/components/WellLoggingOperations/utils';
import type { CurveSettings, Research, RootState } from '@/components/WellLoggingOperations/types';

import type { Gis } from '..';
import {
    CODE_COPY_STRING,
    KEY_COPY_STRING,
} from '@/components/WellLoggingOperations/TabletSettings/ScaleSettings/ScaleSettingsModal';

/**
 * Mapping active researches to well logging curves
 */
export const useGis = (wellKey: string, activeResearches: Research[]): Gis[][] => {
    const curves = useSelector((state: RootState) => state.curves.data);
    const curveSettings = useSelector((state: RootState) => state.curveSettings.data);
    const tabletSettings = useSelector((state: RootState) => state.tabletSettings[wellKey]);

    const gis = useMemo(
        () =>
            activeResearches
                ?.map(_research => {
                    let isCopy = false;
                    let originalResearch = null;
                    if (_research.key.includes(KEY_COPY_STRING)) {
                        isCopy = true;
                        originalResearch = {
                            ..._research,
                            key: _research.key.replace(KEY_COPY_STRING, '').slice(0, -1),
                            code: _research.code.replace(CODE_COPY_STRING, '').slice(0, -1),
                            curve: Number(String(_research.curve).replace('0000', '').slice(0, -1)),
                        };
                    }
                    const research = isCopy ? originalResearch : _research;
                    const curve = curves[_research.curve];
                    // @ts-ignore
                    const rawSettings = curveSettings[research.code];
                    // @ts-ignore
                    const customSettings = tabletSettings?.curveSettings[research.key];
                    const displaySettings =
                        // @ts-ignore
                        tabletSettings?.curveDisplaySettings[research.key] ?? defaultCurveDisplaySettings;

                    if (!curve) {
                        return null;
                    }

                    const settings = {
                        ...rawSettings,
                        ...customSettings,
                        dashed: isCopy,
                    } as CurveSettings;

                    return {
                        ..._research,
                        curveData: curve,
                        settings,
                        displaySettings,
                    } as Gis;
                })
                ?.filter(isDefined),
        [activeResearches, curves, curveSettings, tabletSettings?.curveSettings, tabletSettings?.curveDisplaySettings],
    );

    const result = useMemo(() => {
        const groupedByColumnNum = gis?.reduce((acc, item) => {
            const columnNum = item.settings.columnNum ?? 1;

            const data = acc[columnNum] ?? [];

            const result = [...acc];

            result[columnNum] = data.concat(item);

            return result;
        }, [] as Gis[][]);

        return groupedByColumnNum?.filter(isDefined);
    }, [gis]);

    return result;
};
